<template>
  <div>
    <b-field label="Site">
      <b-dropdown
        multiple
        aria-role="list"
        class="checkbox-dropdown-field-container"
        :disabled="isDisabled"
      >
        <button
          class="button is-primary multi-select-button"
          type="button"
          slot="trigger"
          slot-scope="{ active }"
        >
          <span
            v-if="selectedSiteNames.length > 50 && !isAllSitesSelected"
            class="text-input"
          >
            <b-tooltip
              :label="selectedSiteNames"
              position="is-top"
              size="is-small"
            >
              {{ displaySelectedSiteName }}
            </b-tooltip>
          </span>
          <span v-else class="text-input">
            {{ displaySelectedSiteName }}
          </span>
          <b-icon
            class="dropdown-arrow-icon-down"
            type="is-black"
            :icon="active ? 'chevron-up' : 'chevron-down'"
            size="is-small"
          ></b-icon>
        </button>

        <b-dropdown-item
          value="All Sites"
          class="dropdown-checkbox all-item"
          :custom="true"
        >
          <div class="checkbox-container">
            <div class="column is-1 input-container width-auto">
              <input
                type="checkbox"
                value="true"
                @change="updateAllSelection(false)"
                v-model="isAllSitesSelected"
              />
            </div>
            <div class="column label-container align-left">
              <label
                class="break-next-line text-size-body width-full bold color-bold-text"
                @click="updateAllSelection(true)"
                :class="$style.applyMargin"
              >
                All Sites
              </label>
            </div>
          </div>
        </b-dropdown-item>

        <b-dropdown-item
          :value="value.name"
          aria-role="listitem"
          v-for="(value, index) in auditSites"
          :key="index"
          :class="[$style.dropdownItem, 'dropdown-checkbox']"
          :custom="true"
        >
          <div class="checkbox-container">
            <div class="column is-1 input-container width-auto">
              <input
                type="checkbox"
                :value="value.auditSiteId"
                @change="updateSelection(false, value.auditSiteId)"
                v-model="selectedSites"
              />
            </div>
            <div class="column label-container align-left">
              <label
                class="break-next-line text-size-body"
                @click="updateSelection(true, value.auditSiteId)"
              >
                <span
                  class="break-next-line text-size-body width-full margin-bottom5"
                  >{{ value.name }}</span
                >
              </label>
            </div>
          </div>
        </b-dropdown-item>
      </b-dropdown>
    </b-field>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop } from 'vue-property-decorator';
import { cloneDeep as _cloneDeep } from 'lodash';

@Component({ components: {} })
export default class SiteFilter extends Vue {
  @Prop()
  public auditSites!: any[];
  @Prop()
  public filteredSites!: number[];
  @Prop({ default: false })
  public isDisabled!: boolean;
  public selectedSites: number[] = [];
  public isAllSitesSelected: boolean = true;
  public allSiteIds: number[] = [];
  public selectedSiteNames: string = '';

  get displaySelectedSiteName() {
    if (this.selectedSites.length > 0) {
      const siteNames: any = [];
      this.selectedSites.map((siteId: number) => {
        const selectedAuditSite = this.auditSites.find(
          (auditSite: any) => auditSite.auditSiteId === siteId,
        );
        if (selectedAuditSite) {
          siteNames.push(selectedAuditSite);
        }
      });
      if (siteNames) {
        this.selectedSiteNames = this.convertArrayItemIntoString(siteNames);
      }
      const selectedSiteName =
        this.selectedSiteNames.length > 50
          ? this.selectedSiteNames.substring(0, 50) + '...'
          : this.selectedSiteNames;
      return this.isAllSitesSelected ? 'All Sites' : selectedSiteName;
    } else {
      return 'Select Sites';
    }
  }

  public mounted() {
    if (this.filteredSites) {
      this.populateFilteredData();
    } else {
      this.populateData();
    }
  }

  public populateData() {
    this.auditSites.map((auditSite: any) => {
      this.selectedSites.push(auditSite.auditSiteId);
    });
    this.allSiteIds = _cloneDeep(this.selectedSites);
  }

  public populateFilteredData() {
    this.auditSites.map((auditSite: any) => {
      this.allSiteIds.push(auditSite.auditSiteId);
    });
    this.selectedSites = _cloneDeep(this.filteredSites);
    if (this.allSiteIds.length !== this.selectedSites.length) {
      this.isAllSitesSelected = false;
    }
  }

  public convertArrayItemIntoString(data: any[]) {
    const values = data.map((item: any) => `${item.name}`).join(', ');
    return `${values}`;
  }

  public updateSelection(label: boolean = false, siteId: number) {
    if (label) {
      const siteIndex = this.selectedSites.indexOf(siteId);
      if (siteIndex === -1) {
        this.selectedSites.push(siteId);
      } else {
        this.selectedSites.splice(siteIndex, 1);
      }
    }
    this.isAllSitesSelected =
      this.allSiteIds.length === this.selectedSites.length;
    this.emitSiteFilter();
  }

  public updateAllSelection(label: boolean = false) {
    if (label) {
      this.isAllSitesSelected = !this.isAllSitesSelected;
    }
    if (this.isAllSitesSelected) {
      this.selectedSites = _cloneDeep(this.allSiteIds);
    } else {
      this.selectedSites = [];
    }
    this.emitSiteFilter();
  }

  public mapOrder(selectedSites: number[], auditSiteMapIds: number[]) {
    selectedSites.sort((selectedSite, selectedSiteNext) => {
      if (
        auditSiteMapIds.indexOf(selectedSite) >
        auditSiteMapIds.indexOf(selectedSiteNext)
      ) {
        return 1;
      } else {
        return -1;
      }
    });
    return selectedSites;
  }

  public emitSiteFilter() {
    if (this.selectedSites.length === 0 || this.selectedSites.length === 1) {
      this.$emit('applySiteFilter', this.selectedSites);
    } else {
      const auditSiteMapIds: number[] = this.auditSites.map(
        (auditSite) => auditSite.auditSiteId,
      );
      this.selectedSites = this.mapOrder(this.selectedSites, auditSiteMapIds);
      this.$emit('applySiteFilter', this.selectedSites);
    }
  }
}
</script>
<style lang="scss" module>
.applyMargin {
  margin-left: -2px;
}
.dropdownItem {
  border-bottom: 0.5px solid #dbdbdb;
}
.dropdownItem:last-child {
  border-bottom: unset;
}
</style>
